"use client"

import * as React from "react"
import { useEffect } from "react"
import { useTranslations } from "next-intl"
import {
  IconCalendar,
  IconCalendarEvent,
  IconListCheck,
  IconChartBar,
  IconDashboard,
  IconFileInvoice,
  IconFlag,
  IconList,
  IconPackage,
  IconSettings,
  IconShieldCog,
  IconUsers,
  IconUsersGroup,
  IconUserPlus,
  IconTruck,
  IconBell,
  IconShoppingCart,
  IconHistory,
  IconActivityHeartbeat,
  IconInbox,
  IconArrowsExchange,
  IconCash,
  IconPhoneCall,
  IconPhoneOutgoing,
  IconChartPie,
  IconDental,
  IconHeadset,
  IconMicrophone,
  IconReceipt,
  IconFlask,
  IconNotebook,
  IconStethoscope,
  IconBuildingSkyscraper,
  IconReportMoney,
  IconBellRinging,
  IconIdBadge2,
  IconCalendarWeek,
  IconBeach,
  IconClockCheck,
  IconCashBanknote,
  IconDoor,
  IconCar,
  IconPlaneArrival,
  IconGauge,
  IconCalendarStats,
  IconCoin,
  IconScale,
  IconUserDollar,
  IconRecycle,
  IconFilter,
  IconClockExclamation,
  IconBrandMeta,
  IconBolt,
  IconTargetArrow,
} from "@tabler/icons-react"
import Link from "next/link"
import Image from "next/image"

import { NavMain, type NavSubItem, type NavItemGates } from "@/components/nav-main"
import { resolveReportAccess } from "@/lib/reports"
import { NavSearch, type NavSearchEntry } from "@/components/nav-search"
import { NavSecondary, type NavSecondaryItem } from "@/components/nav-secondary"
import { NavUser } from "@/components/nav-user"
import { AgentStatusSelector } from "@/components/telephony/agent-status-selector"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarTrigger,
} from "@/components/ui/sidebar"
import { useAuthStore } from "@/stores/auth-store"
import { hasRole, ROLES } from "@/lib/roles"
import { useInboxStore } from "@/stores/inbox-store"
import { useRemindersStore } from "@/stores/reminders-store"
import { useWhatsAppStore } from "@/stores/whatsapp-store"
import { useMissedCallsStore } from "@/stores/missed-calls-store"
import { useBranding } from "@/stores/branding-store"
import { ProductGlyph } from "@/components/product-logo"
import { usePermissions } from "@/hooks/use-permissions"
// All permissions that grant access to the Settings page — single source of
// truth in the React-free lib module (derived from the settings sections). This
// intentionally replaces a stale local list that was missing several section
// permissions (whatsapp.accounts.manage, meta-leads.manage, automation.manage,
// templates.manage, settings.notification_templates, settings.appointment_workflow,
// expense_categories.manage), so the Settings gear now shows for every user who
// can reach at least one settings section.
import { SETTINGS_PERMISSIONS } from "@/lib/settings-sections"
import { resolveDashboardRole } from "@/lib/dashboard-role"
import type { FeatureFlags } from "@/types"

// Rooms / Salla: the backend emits `rooms_enabled` in the user's feature_flags
// payload, but the shared FeatureFlags union doesn't declare the key yet — the
// rooms pages read it through the same cast (see salla/page.tsx). Casting here
// keeps the nav gates type-clean without touching the shared types module.
const ROOMS_FLAG = "rooms_enabled" as unknown as keyof FeatureFlags
/**
 * ArtDent 260930 — the accountant only confirms card and bank money. Under this
 * flag that role keeps Dashboard, Finance › Payments, HR and Settings; every
 * other rail is hidden (not revoked — a payment's link to its invoice still
 * opens). Admin is exempt, as with every role-hide rule.
 */
const ACCOUNTANT_FOCUS = {
  roles: ["accountant"],
  flag: "accountant_confirmations_focus_enabled" as keyof FeatureFlags,
}

// Appointments list restoration: trunk collapsed Appointments to a flat
// /calendar link. Clinics that pin `legacy_list_enabled` get the
// List/Calendar accordion back — applied in decorateItem (NOT as a static
// `items` array), because filterSubItems drops a parent whose children all
// gate out, and a flag-off clinic must keep the flat link byte-for-byte.
const APPOINTMENT_VIEW_SUBITEMS = [
  {
    title: "List View",
    titleKey: "appointmentsListView",
    url: "/appointments",
    icon: IconList,
  },
  {
    title: "Calendar View",
    titleKey: "appointmentsCalendarView",
    url: "/calendar",
    icon: IconCalendar,
  },
]

// Nav definitions carry i18n keys (titleKey / labelKey / subTitleKey) into the
// `nav` namespace; the labels are resolved to display strings at render time in
// AppSidebar via useTranslations('nav') (Phase 71 I18N-05, central chrome).
const navGroups = [
  {
    items: [
      {
        title: "Dashboard",
        titleKey: "dashboard",
        url: "/",
        icon: IconDashboard,
        iconColor: "text-primary",
      },
      {
        // An affiliate partner's entire sidebar. `leads.view.affiliated` is
        // held by no other role, so this row appears for them and nowhere
        // else — and every other group below gates itself out, because the
        // role holds none of their permissions.
        title: "My Patients",
        titleKey: "affiliatorPatients",
        url: "/affiliator/patients",
        icon: IconUserPlus,
        iconColor: "text-primary",
        permission: "leads.view.affiliated",
      },
      {
        // ArtDent 261013 — the partner's own report over the patients they
        // referred. Rides the same key as the row above plus the clinic's
        // flag; it is NOT the Referral Partners entry under Reports, which
        // is the clinic's league table behind a reports.* key.
        title: "My Reports",
        titleKey: "affiliatorReports",
        url: "/affiliator/reports",
        icon: IconChartBar,
        iconColor: "text-primary",
        permission: "leads.view.affiliated",
        featureFlag: "affiliator_own_report_enabled" as keyof FeatureFlags,
      },
    ],
  },
  {
    // Pinned quick access (sidebar redesign, DenteX spec 2026-07-26): the four
    // highest-traffic destinations as flat icon rows above the workspace
    // sections. Deliberate duplication — each also lives in its taxonomy
    // section; gates are copied verbatim from the section counterpart, and
    // decorateItem (badges, simple-clinic URL rewrite) matches by title so it
    // applies to both copies.
    label: "Pinned",
    labelKey: "groups.pinned",
    items: [
      {
        title: "Leads",
        titleKey: "leads",
        url: "/leads",
        icon: IconUserPlus,
        anyPermission: ["leads.view.all", "leads.view.assigned", "leads.view.travelling"],
        hideForRolesWhenFlag: [
          // ArtDent 260917 — a doctor's rail is the chair, not the pipeline.
          {
            roles: ["doctor"],
            flag: "doctor_clinical_focus_enabled" as keyof FeatureFlags,
          },
          // ArtDent 260929 — the coordinator works from Clients. The row
          // cannot be taken away on the roles screen: `leads.view.all` lights
          // it, and the same grant is what opens a patient record at all.
          {
            roles: ["coordinator"],
            flag: "coordinator_clients_focus_enabled" as keyof FeatureFlags,
          },
          ACCOUNTANT_FOCUS,
        ],
      },
      {
        title: "Inbox",
        titleKey: "inbox",
        url: "/inbox",
        hideForRolesWhenFlag: ACCOUNTANT_FOCUS,
        icon: IconInbox,
        permission: "whatsapp.view",
        featureFlag: "whatsapp_enabled" as keyof FeatureFlags,
        hideWhenFlag: "hide_inbox_enabled" as keyof FeatureFlags,
      },
      {
        title: "Appointments",
        titleKey: "appointments",
        url: "/calendar",
        hideForRolesWhenFlag: ACCOUNTANT_FOCUS,
        icon: IconCalendarEvent,
        permission: "appointments.view",
      },
      {
        title: "Tasks",
        titleKey: "tasks",
        url: "/tasks",
        hideForRolesWhenFlag: ACCOUNTANT_FOCUS,
        icon: IconListCheck,
        permission: "tasks.view.assigned",
        featureFlag: "tasks_enabled" as keyof FeatureFlags,
      },
      {
        title: "Clients",
        titleKey: "clients",
        url: "/clients",
        icon: IconUsers,
        permission: "clients.view",
        hideWhenFlag: "patients_terminology_enabled" as keyof FeatureFlags,
        hideForRolesWhenFlag: ACCOUNTANT_FOCUS,
      },
    ],
  },
  {
    // Function-first taxonomy (Sales / Clinic / Finance / HR / Telephony /
    // Reports): groups map to who uses them — agents live in Sales, doctors
    // and front-desk in Clinic, the accountant in Finance — so the persisted
    // per-group collapse keeps each role's rail short.
    label: "Sales",
    labelKey: "groups.sales",
    id: "sales",
    defaultOpen: true,
    // ArtDent 260917 — the whole section, not row by row: a doctor has no
    // business in the pipeline, and leaving an empty "Sales" heading behind
    // would be worse than removing it.
    hideForRolesWhenFlag: [
      {
        roles: ["doctor"],
        flag: "doctor_clinical_focus_enabled" as keyof FeatureFlags,
      },
      ACCOUNTANT_FOCUS,
    ],
    items: [
      {
        title: "Leads",
        titleKey: "leads",
        url: "/leads",
        icon: IconUserPlus,
        iconColor: "text-chart-2",
        anyPermission: ["leads.view.all", "leads.view.assigned", "leads.view.travelling"],
        // ArtDent 260929 — same rule as the pinned copy above.
        hideForRolesWhenFlag: {
          roles: ["coordinator"],
          flag: "coordinator_clients_focus_enabled" as keyof FeatureFlags,
        },
      },
      {
        title: "Italian Leads",
        titleKey: "italianLeads",
        url: "/leads/italian",
        icon: IconFlag,
        iconColor: "text-chart-3",
        permission: "italian_leads.view",
        featureFlag: "italian_leads_view_enabled" as keyof FeatureFlags,
      },
      {
        title: "Recycle Queue",
        titleKey: "recycleQueue",
        url: "/leads/recycle",
        icon: IconRecycle,
        iconColor: "text-chart-3",
        permission: "leads.recycle",
        featureFlag: "lead_recycling_enabled" as keyof FeatureFlags,
      },
      {
        title: "Inbox",
        titleKey: "inbox",
        url: "/inbox",
        icon: IconInbox,
        iconColor: "text-chart-1",
        permission: "whatsapp.view",
        featureFlag: "whatsapp_enabled" as keyof FeatureFlags,
        hideWhenFlag: "hide_inbox_enabled" as keyof FeatureFlags,
      },
      {
        title: "Quotes",
        titleKey: "quotes",
        url: "/quotes",
        icon: IconFileInvoice,
        iconColor: "text-chart-5",
        permission: "quotes.view",
        // Simple clinic mode: small walk-in clinics don't run the quoting
        // workflow. See FEATURE_SIMPLE_CLINIC_MODE. Invoice-style quotes
        // (Albdental 260810) bring the row back — the quote becomes a plain
        // invoice-like document, so the hide no longer applies.
        hideWhenFlag: "simple_clinic_mode" as keyof FeatureFlags,
        unhideFlag: "invoice_style_quotes_enabled" as keyof FeatureFlags,
      },
    ],
  },
  {
    label: "Clinic",
    labelKey: "groups.clinic",
    id: "clinic",
    hideForRolesWhenFlag: ACCOUNTANT_FOCUS,
    // Sections are an exclusive accordion since the sidebar redesign — Sales
    // is the single default-open branch; Pinned covers Clinic's hot links.
    defaultOpen: false,
    items: [
      {
        title: "Clients",
        titleKey: "clients",
        url: "/clients",
        icon: IconUsers,
        iconColor: "text-chart-2",
        permission: "clients.view",
        // Per-clinic: clinics using "Patients" terminology collapse the Lead/Client split,
        // so the Clients item is hidden (route + data stay intact). See FEATURE_PATIENTS_TERMINOLOGY.
        hideWhenFlag: "patients_terminology_enabled" as keyof FeatureFlags,
      },
      {
        title: "Patient charts",
        titleKey: "kartele",
        url: "/kartele",
        icon: IconNotebook,
        iconColor: "text-chart-2",
        permission: "kartele.view",
        featureFlag: "simple_kartele_enabled" as keyof FeatureFlags,
      },
      {
        title: "Appointments",
        titleKey: "appointments",
        url: "/calendar",
        icon: IconCalendarEvent,
        iconColor: "text-chart-4",
        permission: "appointments.view",
      },
      {
        // Rooms / Salla — the live treatment-room reception board. Gated on
        // rooms_enabled + the same permission as the Calendar link it sits
        // beside (appointments.view). Literal title (no titleKey): the room
        // i18n catalog is owned elsewhere, so this renders the English label
        // (matching the /rooms page title) on every locale rather than leak a
        // dotted key-path.
        title: "Rooms",
        url: "/rooms",
        icon: IconDoor,
        iconColor: "text-chart-1",
        permission: "appointments.view",
        featureFlag: ROOMS_FLAG,
      },
      {
        // Logistics / driver routing — the daily driver dispatch board (airport
        // pickups, drop-offs, hotel↔clinic shuttles). Sits directly under Rooms:
        // both are front-desk day-of-operations surfaces. `logistics_enabled` IS
        // declared on the FeatureFlags type, so no cast is needed here.
        title: "Logistics",
        titleKey: "logistics",
        url: "/logistics",
        icon: IconCar,
        iconColor: "text-chart-3",
        permission: "logistics.view",
        featureFlag: "logistics_enabled" as keyof FeatureFlags,
      },
      {
        // Travel Desk — the trip ROSTER, beside the dispatch board it is not:
        // that one is "which car, which driver, what time today", this one is
        // "who is travelling this month, referred by whom, and are they booked".
        //
        // 🟥 `travel_desk.view` is granted to NO ROLE and handed out per user
        // (`php artisan travel-desk:access <email>`), so on a clinic with
        // several coordinators this row appears for exactly the one who owns
        // travel. That is the feature, not an oversight — do not "fix" it by
        // widening the permission to logistics.view, which every coordinator
        // holds.
        title: "Travel Desk",
        titleKey: "travelDesk",
        url: "/travel-desk",
        icon: IconPlaneArrival,
        iconColor: "text-chart-2",
        permission: "travel_desk.view",
        featureFlag: "travel_desk_enabled" as keyof FeatureFlags,
      },
      {
        title: "Lab Orders",
        titleKey: "labOrders",
        url: "/lab-orders",
        icon: IconFlask,
        iconColor: "text-sky-500",
        permission: "lab_orders.view",
        hideWhenFlag: "simple_clinic_mode" as keyof FeatureFlags,
      },
      // Inventory rows — flattened out of the former "Inventory" accordion
      // container (sidebar redesign: workspace sections are one level deep).
      // Each row now carries the container's inventory_enabled gate ANDed with
      // its own permission/mode gates, so per-clinic visibility is unchanged
      // (e.g. Suppliers/POs stay visible in simple_clinic_mode and to users
      // lacking inventory.view).
      {
        title: "Items",
        titleKey: "inventoryItems",
        url: "/inventory",
        icon: IconPackage,
        permission: "inventory.view",
        featureFlag: "inventory_enabled" as keyof FeatureFlags,
        hideWhenFlag: "simple_clinic_mode" as keyof FeatureFlags,
      },
      {
        title: "Stock Movements",
        titleKey: "inventoryStockMovements",
        url: "/inventory/movements",
        icon: IconArrowsExchange,
        permission: "inventory.view",
        featureFlag: "inventory_enabled" as keyof FeatureFlags,
        hideWhenFlag: "simple_clinic_mode" as keyof FeatureFlags,
      },
      {
        title: "Suppliers",
        titleKey: "suppliers",
        url: "/suppliers",
        icon: IconTruck,
        permission: "suppliers.view",
        featureFlag: "inventory_enabled" as keyof FeatureFlags,
        hideWhenFlag: "inventory_lite_mode" as keyof FeatureFlags,
      },
      {
        title: "Purchase Orders",
        titleKey: "purchaseOrders",
        url: "/purchase-orders",
        icon: IconShoppingCart,
        permission: "inventory.orders",
        featureFlag: "inventory_enabled" as keyof FeatureFlags,
        hideWhenFlag: "inventory_lite_mode" as keyof FeatureFlags,
      },
    ],
  },
  {
    label: "Finance",
    labelKey: "groups.finance",
    id: "finance",
    defaultOpen: false,
    items: [
      {
        title: "Invoices",
        titleKey: "invoices",
        url: "/invoices",
        hideForRolesWhenFlag: ACCOUNTANT_FOCUS,
        icon: IconReceipt,
        iconColor: "text-chart-2",
        permission: "invoices.view",
      },
      {
        // All-payments list (cross-invoice). Harvested with the /payments
        // endpoint; nav.payments already existed in en/it, the row did not — so
        // the page was unreachable except by typing the URL.
        title: "Payments",
        titleKey: "payments",
        url: "/payments",
        icon: IconCoin,
        iconColor: "text-chart-1",
        permission: "invoices.view",
        featureFlag: "payments_enabled" as keyof FeatureFlags,
      },
      {
        title: "Costs",
        titleKey: "costs",
        url: "/costs",
        hideForRolesWhenFlag: ACCOUNTANT_FOCUS,
        icon: IconCash,
        iconColor: "text-chart-5",
        permission: "costs.view",
        featureFlag: "costs_enabled" as keyof FeatureFlags,
        hideWhenFlag: "simple_clinic_mode" as keyof FeatureFlags,
      },
    ],
  },
  {
    // HR as a top-level group (flat items — the group collapse replaces the
    // former accordion): Staff (Phase 1, hr_core_enabled) + Doctors +
    // Roster (Phase 2) + Leave (Phase 3) + Attendance (Phase 4) +
    // Payroll (Phase 5).
    label: "HR",
    labelKey: "groups.hr",
    id: "hr",
    defaultOpen: false,
    items: [
      {
        title: "Staff",
        titleKey: "hrStaff",
        url: "/hr/staff",
        icon: IconIdBadge2,
        iconColor: "text-chart-2",
        permission: "hr.view",
        featureFlag: "hr_core_enabled" as keyof FeatureFlags,
      },
      {
        title: "Roster",
        titleKey: "hrRoster",
        url: "/hr/roster",
        icon: IconCalendarWeek,
        iconColor: "text-chart-4",
        permission: "hr.roster.view",
        featureFlag: "hr_roster_enabled" as keyof FeatureFlags,
      },
      {
        // Phase 3 — leave / time-off. Visible to anyone who can request their
        // own leave OR approve the team's (managers hold approve too).
        title: "Leave",
        titleKey: "hrLeave",
        url: "/hr/leave",
        icon: IconBeach,
        iconColor: "text-chart-3",
        anyPermission: ["hr.leave.approve", "hr.leave.request"],
        featureFlag: "hr_leave_enabled" as keyof FeatureFlags,
      },
      {
        // Phase 4 — attendance / check-ins. Visible to anyone who can view the
        // team's attendance OR record their own (all staff hold the self perm).
        title: "Attendance",
        titleKey: "hrAttendance",
        url: "/hr/attendance",
        icon: IconClockCheck,
        iconColor: "text-chart-1",
        anyPermission: ["hr.attendance.view", "hr.attendance.self"],
        featureFlag: "hr_attendance_enabled" as keyof FeatureFlags,
      },
      {
        // Phase 5 — payroll gross register + CSV export (managers only).
        title: "Payroll",
        titleKey: "hrPayroll",
        url: "/hr/payroll",
        icon: IconCashBanknote,
        iconColor: "text-chart-5",
        permission: "hr.payroll.manage",
        featureFlag: "hr_payroll_export_enabled" as keyof FeatureFlags,
      },
    ],
  },
  {
    label: "Telephony",
    labelKey: "groups.telephony",
    id: "telephony",
    hideForRolesWhenFlag: ACCOUNTANT_FOCUS,
    defaultOpen: false,
    items: [
      {
        title: "Call Analytics",
        titleKey: "callAnalytics",
        url: "/call-analytics",
        icon: IconPhoneCall,
        iconColor: "text-chart-4",
        // A report (calls domain) that lives in the telephony group: gates
        // come from the registry like every other report entry.
        report: "call-analytics",
      },
      {
        title: "Supervisor",
        titleKey: "supervisor",
        url: "/supervisor",
        icon: IconHeadset,
        iconColor: "text-chart-1",
        permission: "calls.view",
        featureFlag: "telephony_enabled" as keyof FeatureFlags,
        hideWhenFlag: "hide_telephony_pages_enabled" as keyof FeatureFlags,
      },
      {
        // Phase 75 — outbound-dialer campaign management. Gated on its own flag
        // (auto_dialer_enabled, default false) + campaigns.manage (admin-only).
        title: "Campaigns",
        titleKey: "campaigns",
        url: "/campaigns",
        icon: IconPhoneOutgoing,
        iconColor: "text-chart-3",
        permission: "campaigns.manage",
        featureFlag: "auto_dialer_enabled" as keyof FeatureFlags,
      },
      {
        // Mic-usage analytics — gated on its own feature flag (independent
        // of telephony), not the telephony hide-flag. mic_usage.view is
        // seeded to admin and grantable to other roles.
        title: "Mic Monitor",
        titleKey: "micMonitor",
        url: "/mic-monitor",
        icon: IconMicrophone,
        iconColor: "text-chart-2",
        permission: "mic_usage.view",
        featureFlag: "mic_monitoring_enabled" as keyof FeatureFlags,
      },
    ],
  },
  {
    // Every report surface in one place — flat items (the collapsible group
    // replaces the former Reports accordion); each row keeps its own
    // permission/flag gates. Users/Activity Log moved to the footer nav
    // (admin plumbing, not workflow).
    label: "Reports",
    labelKey: "groups.reports",
    id: "reports",
    hideForRolesWhenFlag: ACCOUNTANT_FOCUS,
    defaultOpen: false,
    items: [
      {
        title: "Overview",
        titleKey: "reportsOverview",
        url: "/reports",
        report: "overview",
        icon: IconChartBar,
        iconColor: "text-chart-5",
      },
      {
        // Every chart here reads the classification group (source breakdown,
        // status conversion rates, volume by source), so a clinic that removed
        // status/source/assigned-to has an empty report, not a thin one — it
        // leaves with them. EKDent 260831; the flag defaults ON, so no other
        // clinic is affected.
        title: "Lead Analytics",
        titleKey: "leadAnalytics",
        url: "/lead-analytics",
        report: "lead-analytics",
        icon: IconChartPie,
        iconColor: "text-chart-3",
      },
      {
        // Takes the Lead Analytics slot on the clinics that trade one for the
        // other. Promoted out of the /reports overview card list (260831) —
        // it was the only pilot report reachable solely by scrolling the
        // overview. Pilot flag defaults OFF, so this is EKDent-only today.
        title: "Work Performed",
        titleKey: "workPerformed",
        url: "/reports/work-performed",
        report: "work-performed",
        icon: IconDental,
        iconColor: "text-chart-3",
      },
      {
        title: "Visit Categorization",
        titleKey: "visitCategorization",
        url: "/visit-categorization",
        report: "visit-categorization",
        icon: IconStethoscope,
        iconColor: "text-chart-2",
      },
      {
        title: "Debtors",
        titleKey: "debtors",
        url: "/reports/debtors",
        report: "debtors",
        icon: IconReportMoney,
        iconColor: "text-chart-1",
      },
      {
        // Hotel expenses — accommodation payables, aged like debtors. Dual-gated
        // (one registry slug — flags, hide-modes and domain permission come from lib/reports.ts).
        title: "Hotel Expenses",
        titleKey: "hotelExpenses",
        url: "/reports/hotel-expenses",
        report: "hotel-expenses",
        icon: IconBuildingSkyscraper,
        iconColor: "text-chart-2",
      },
      {
        // The money-OUT twin of Debtors, next to it on purpose: the pair reads
        // as one question asked in both directions. Gated on the PO payment
        // ledger, so it appears only where there is settlement data to age.
        title: "Supplier Payables",
        titleKey: "supplierPayables",
        url: "/reports/supplier-payables",
        report: "supplier-payables",
        icon: IconFileInvoice,
        iconColor: "text-chart-4",
      },
      {
        // Financial suite — generic P&L (income vs expense categories by
        // month). The API nests /reports/profit-loss inside BOTH costs_enabled
        // AND expense_engine_enabled (404s if either is off), so gate on both
        // via the registry entry — matching the /reports card.
        title: "P&L",
        titleKey: "profitLoss",
        url: "/reports/profit-loss",
        report: "profit-loss",
        icon: IconScale,
        iconColor: "text-chart-5",
      },
      {
        // Financial suite (Stage 0) — doctor production report. Gated exactly
        // like the report endpoint (reports.finance OR reports.finance.own +
        // doctor_production_enabled). reports.finance.own is the doctors'
        // doctors' self-service grant — the ownership scopes trim the data
        // to their own row, so the same page serves both audiences.
        title: "Doctor Production",
        titleKey: "doctorProduction",
        url: "/reports/doctor-production",
        report: "doctor-production",
        icon: IconUserDollar,
        iconColor: "text-chart-4",
      },
      {
        // Doctor commissions earned in a window (EKDent 260906). compensation
        // domain, clinic-wide key ONLY — no own form like the production report: this
        // page shows every doctor's earnings and has no per-doctor scoping,
        // so a doctor's self-service grant must not open it.
        title: "Doctor Commissions",
        titleKey: "doctorCommissions",
        url: "/reports/doctor-commissions",
        report: "doctor-commissions",
        icon: IconUserDollar,
        iconColor: "text-chart-4",
      },
      // Reports redesign (wave 1) — funnel / team performance / follow-ups /
      // finance overview. Each entry resolves through the registry (domain key + its OWN
      // feature flag) to exactly match its API route's gates.
      {
        title: "Funnel",
        titleKey: "funnelReport",
        url: "/reports/funnel",
        report: "funnel",
        icon: IconFilter,
        iconColor: "text-chart-1",
      },
      {
        title: "Team Performance",
        titleKey: "teamPerformance",
        url: "/reports/team-performance",
        report: "team-performance",
        icon: IconUsersGroup,
        iconColor: "text-chart-3",
      },
      {
        title: "Consultants",
        titleKey: "consultantReport",
        url: "/reports/consultants",
        report: "consultants",
        icon: IconUsersGroup,
        iconColor: "text-chart-2",
      },
      {
        // Admin-only by default, unlike its neighbours: compensation domain,
        // because this lines up every partner's commercial
        // numbers side by side.
        title: "Referral Partners",
        titleKey: "affiliators",
        url: "/reports/affiliators",
        report: "affiliators",
        icon: IconUsersGroup,
        iconColor: "text-chart-2",
      },
      {
        title: "Follow-ups",
        titleKey: "followUps",
        url: "/reports/follow-ups",
        report: "follow-ups",
        icon: IconClockExclamation,
        iconColor: "text-chart-4",
      },
      {
        title: "Finance",
        titleKey: "financeOverview",
        url: "/reports/finance",
        report: "finance",
        icon: IconCash,
        iconColor: "text-chart-5",
      },
      {
        // Phase 79 (RECALL-02) — recall worklist nav entry, gated exactly
        // like the Debtors row above (recalls.view permission, its own
        // feature flag).
        title: "Recalls",
        titleKey: "recalls",
        url: "/reports/recalls",
        icon: IconBellRinging,
        iconColor: "text-chart-2",
        permission: "recalls.view",
        // No simple_clinic_mode hide (dropped 2026-08-05): walk-in clinics
        // are exactly who works a recall list — the flag pair
        // (recalls_enabled + recalls.view) is the real gate.
        featureFlag: "recalls_enabled" as keyof FeatureFlags,
      },
      {
        // Phase 80 (CCR-05..09) — call-center reports nav entry. Deliberately
        // gated on the calls domain (registry) to
        // match the report endpoints' own permission (80-UI-SPEC §12 C10).
        title: "Call Reports",
        titleKey: "callReports",
        url: "/reports/call-center",
        report: "call-center",
        icon: IconPhoneCall,
        iconColor: "text-chart-3",
      },
      {
        // Rooms / Salla — chair-utilization report. Gated exactly like the
        // Debtors sibling above (finance/clinical domain + its feature flag).
        // Literal title mirrors the sibling English nav labels + the /reports/
        // room-utilization page title.
        title: "Room Utilization",
        url: "/reports/room-utilization",
        report: "room-utilization",
        icon: IconGauge,
        iconColor: "text-chart-2",
      },
      {
        // Rooms / Salla — empty-chair (gaps) report.
        title: "Room Gaps",
        url: "/reports/room-gaps",
        report: "room-gaps",
        icon: IconCalendarStats,
        iconColor: "text-chart-4",
      },
      {
        // Rooms / Salla — production-per-room report. Needs BOTH rooms_enabled
        // and payments_enabled (it attributes revenue), so it AND-gates the two
        // via allFeatureFlags — mirroring how the Debtors card guards its own
        // payments dependency, but adding the rooms gate on top so the link
        // never dead-ends on a payments-off / rooms-on clinic.
        title: "Room Production",
        url: "/reports/room-production",
        report: "room-production",
        icon: IconCoin,
        iconColor: "text-chart-5",
      },
      // Meta Marketing Suite — the four /reports/meta-* pages. Deliberately
      // ONE flag for all four (they read one dataset and only make sense
      // together), so every entry carries the SAME gates as its API route:
      // reports.sales + meta_marketing_reports_enabled.
      {
        title: "Meta Funnel",
        titleKey: "metaFunnelReport",
        url: "/reports/meta-funnel",
        report: "meta-funnel",
        icon: IconBrandMeta,
        iconColor: "text-chart-1",
      },
      {
        title: "Meta Speed to Lead",
        titleKey: "metaSpeedToLeadReport",
        url: "/reports/meta-speed-to-lead",
        report: "meta-speed-to-lead",
        icon: IconBolt,
        iconColor: "text-chart-2",
      },
      {
        title: "Meta ROAS",
        titleKey: "metaRoasReport",
        url: "/reports/meta-roas",
        report: "meta-roas",
        icon: IconCoin,
        iconColor: "text-chart-3",
      },
      {
        title: "Meta Budget",
        titleKey: "metaBudgetReport",
        url: "/reports/meta-budget",
        report: "meta-budget",
        icon: IconTargetArrow,
        iconColor: "text-chart-4",
      },
    ],
  },
]

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const t = useTranslations('nav')
  const { user, isAuthenticated } = useAuthStore()
  const branding = useBranding()
  const { unreadCount, startPolling, stopPolling } = useRemindersStore()
  const { accountStatuses, fetchStatus: fetchWhatsAppStatus } = useWhatsAppStore()
  const { accountUnreadCounts, setActiveAccount, activeAccountId } = useInboxStore()
  const { count: missedCallsCount, startPolling: startMissedPolling, stopPolling: stopMissedPolling } = useMissedCallsStore()

  // Start polling for reminders when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      startPolling(30000) // Poll every 30 seconds
    }
    return () => {
      stopPolling()
    }
  }, [isAuthenticated, startPolling, stopPolling])

  // Start polling for missed calls when authenticated (only when telephony is
  // enabled — the endpoint is flag-gated, so telephony-off clinics would poll
  // a 404 every minute)
  const telephonyEnabled = user?.feature_flags?.telephony_enabled ?? false
  useEffect(() => {
    if (isAuthenticated && telephonyEnabled) {
      startMissedPolling(60000) // Poll every 60 seconds
    }
    return () => {
      stopMissedPolling()
    }
  }, [isAuthenticated, telephonyEnabled, startMissedPolling, stopMissedPolling])

  // Fetch WhatsApp status on mount and periodically (only when enabled)
  const whatsappEnabled = user?.feature_flags?.whatsapp_enabled ?? false
  useEffect(() => {
    if (isAuthenticated && whatsappEnabled) {
      fetchWhatsAppStatus()
      const interval = setInterval(fetchWhatsAppStatus, 60000) // Refresh every 60 seconds
      return () => clearInterval(interval)
    }
  }, [isAuthenticated, whatsappEnabled, fetchWhatsAppStatus])

  const userData = {
    name: user ? `${user.first_name} ${user.last_name}` : "User",
    email: user?.email || "",
    // Doctor Profiles: doctor's photo thumb (whenLoaded doctorProfile); "" for
    // non-doctors/no photo → NavUser falls back to initials.
    avatar: user?.avatar_url ?? "",
  }

  const { can: hasPermission, canAny: hasAnyPermission } = usePermissions()

  // Per-WhatsApp-account rows under the pinned Inbox item (owner ask,
  // 2026-07-26 — restored after the sidebar redesign dropped them): name,
  // per-account unread badge, click switches the active account. Attached to
  // the PINNED copy only; the Sales-section copy stays a flat row.
  const inboxSubItems = Object.entries(accountStatuses).map(([id, info]) => ({
    title: info.name || info.phoneNumber || id,
    url: "/inbox",
    badge: accountUnreadCounts[id] ?? 0,
    onClick: () => {
      setActiveAccount(id)
    },
    isActive: activeAccountId === id,
  }))

  // Filter each group's items by permission/feature flag, drop empty groups.
  type RawItem = (typeof navGroups)[number]["items"][number]
  // Rooms/finance extend the shared gate set with two AND-mechanisms (see the
  // Salla Production / P&L sub-items): `allFeatureFlags` AND-gates a list of
  // flags; `featureFlagAlso` is a second flag that must ALSO be on.
  type RoleHideRule = { roles: string[]; flag: keyof FeatureFlags }
  type GatesWithAnd = NavItemGates & {
    allFeatureFlags?: (keyof FeatureFlags)[]
    featureFlagAlso?: keyof FeatureFlags
    // Cancels hideWhenFlag when set and ON (e.g. invoice_style_quotes_enabled
    // brings Quotes back for a simple_clinic_mode clinic).
    unhideFlag?: keyof FeatureFlags
    /**
     * ArtDent 260917 — hide this row (or a whole group) from certain ROLES,
     * and only where the clinic has asked. `role` above is the mirror image: it
     * REQUIRES a role. This takes one away, which is why it carries its own
     * flag — a role is fleet-wide, and a rail trimmed for every clinic's
     * doctors is not what anybody asked for.
     */
    hideForRolesWhenFlag?: RoleHideRule | RoleHideRule[]
  }
  // Shared by the item gate and the GROUP filter below, so a row and the
  // section holding it cannot disagree about who may see it.
  const hiddenForRole = (gates: {
    hideForRolesWhenFlag?: RoleHideRule | RoleHideRule[]
  }): boolean => {
    if (!gates.hideForRolesWhenFlag) return false

    // Admin wins: an owner who also carries the doctor role must not lose
    // their own rail.
    if (user?.roles?.includes("admin")) return false

    // Several clinics' asks can land on one row; any rule that applies hides it.
    return [gates.hideForRolesWhenFlag].flat().some(
      (rule) =>
        user?.feature_flags?.[rule.flag] === true &&
        rule.roles.some((r) => user?.roles?.includes(r) ?? false)
    )
  }
  const passesGates = (gates: GatesWithAnd): boolean => {
    // Report entries carry ONE gate — the registry slug — and resolve flags,
    // hide-modes and the domain permission (clinic or own) from the same
    // generated table the page's <ReportGuard> and the API route use.
    if (
      gates.report &&
      !resolveReportAccess(
        gates.report,
        user?.permissions,
        user?.feature_flags,
        user?.report_permissions
      ).allowed
    )
      return false
    if (gates.featureFlag && !user?.feature_flags?.[gates.featureFlag]) return false
    if (gates.allFeatureFlags && !gates.allFeatureFlags.every((f) => user?.feature_flags?.[f])) return false
    if (gates.featureFlagAlso && !user?.feature_flags?.[gates.featureFlagAlso]) return false
    if (
      gates.hideWhenFlag &&
      user?.feature_flags?.[gates.hideWhenFlag] &&
      !(gates.unhideFlag && user?.feature_flags?.[gates.unhideFlag])
    )
      return false
    // Mirror of hideWhenFlag for opt-OUT (default-true) flags: `=== false`, so
    // a payload missing the key leaves the item visible. See NavSubItem.
    if (gates.hideWhenFlagOff && user?.feature_flags?.[gates.hideWhenFlagOff] === false)
      return false
    if (hiddenForRole(gates)) return false
    if (gates.role && !(user?.roles?.includes(gates.role) ?? false)) return false
    if (gates.permission && !hasPermission(gates.permission)) return false
    // Phase 45 (D-12, D-26): support OR-of-permissions — /leads nav item is visible
    // when user has `leads.view.all` OR `leads.view.assigned` (but NOT `leads.reference`-only).
    if (gates.anyPermission && !hasAnyPermission(gates.anyPermission)) return false
    return true
  }
  const isItemVisible = (item: RawItem) => passesGates(item as GatesWithAnd)
  // Nested `items` sub-arrays aren't covered by isItemVisible above (that only
  // filters top-level group items), so each sub-item's full gate set
  // (permission / anyPermission / featureFlag / hideWhenFlag / role) is
  // evaluated here via passesGates. When filtering empties the sub-array, the
  // PARENT is dropped entirely (`null`, stripped from the pipeline) — never
  // flattened to a dead link.
  const filterSubItems = (item: RawItem) => {
    const subItems = (item as { items?: NavSubItem[] }).items
    if (!subItems) return item
    const visible = subItems.filter((sub) => passesGates(sub))
    if (visible.length === 0) return null // drop parent — never flatten to a dead link
    return { ...item, items: visible }
  }
  // Accepts the filtered shape (sub-items narrowed to NavSubItem[]), not just RawItem.
  const decorateItem = (item: NonNullable<ReturnType<typeof filterSubItems>>, isPinnedGroup = false) => {
    // Simple clinic mode: the calendar IS the appointments surface — collapse
    // the Appointments accordion to a flat link straight to /calendar.
    if (item.title === "Appointments" && user?.feature_flags?.simple_clinic_mode) {
      return { ...item, url: "/calendar", items: undefined }
    }
    // Appointments list restored behind a flag — expand the section row into
    // List/Calendar. Pinned stays a one-click /calendar link (pinned rows are
    // hot links, not accordions), and simple clinic mode above still wins.
    if (
      item.title === "Appointments" &&
      !isPinnedGroup &&
      user?.feature_flags?.legacy_list_enabled
    ) {
      return { ...item, items: APPOINTMENT_VIEW_SUBITEMS }
    }
    if (item.title === "Inbox") {
      // Phase 48 INBOX-05 — sum per-account unread for the badge; the pinned
      // copy additionally expands into the per-account switcher rows.
      const totalUnread = Object.values(accountUnreadCounts).reduce((a, b) => a + b, 0)
      return {
        ...item,
        items: isPinnedGroup && inboxSubItems.length > 0 ? inboxSubItems : undefined,
        badge: totalUnread > 0 ? totalUnread : undefined,
      }
    }
    if (item.title === "Call Analytics") {
      return {
        ...item,
        badge: missedCallsCount > 0 ? missedCallsCount : undefined,
      }
    }
    return item
  }
  // Resolve i18n keys → display strings (nav namespace). Item/sub-item titleKeys
  // and group labelKeys map to translated labels; data-driven titles (e.g. inbox
  // account names) keep their runtime value.
  const translateItem = (item: ReturnType<typeof decorateItem>) => {
    const titleKey = (item as { titleKey?: string }).titleKey
    const subItems = (item as { items?: NavSubItem[] }).items
    return {
      ...item,
      title: titleKey ? t(titleKey) : item.title,
      items: subItems?.map((sub) => ({
        ...sub,
        title: sub.titleKey ? t(sub.titleKey) : sub.title,
      })),
    }
  }
  // Simple clinic mode also trims the secondary nav (Notifications + Help
  // Center) and flattens the main nav below — same rationale as the
  // hideWhenFlag rows above.
  const simpleClinicMode = user?.feature_flags?.simple_clinic_mode ?? false

  const filteredNavGroups = navGroups
    .map((group) => ({
      ...group,
      label: (group as { labelKey?: string }).labelKey
        ? t((group as { labelKey?: string }).labelKey as string)
        : group.label,
      items: group.items
        .filter(isItemVisible)
        .map(filterSubItems)
        .filter((i): i is NonNullable<typeof i> => i !== null)
        .map((item) =>
          decorateItem(item, (group as { labelKey?: string }).labelKey === "groups.pinned")
        )
        .map(translateItem),
    }))
    // A whole section can be withheld from a role, not only its rows.
    .filter((group) => !hiddenForRole(group as GatesWithAnd))
    .filter((group) => group.items.length > 0)

  // Simple clinic mode: the taxonomy collapses to ~6 visible destinations, so
  // grouped sections and accordions are pure overhead — flatten everything the
  // gates let through into one non-collapsible list. Dedupe by URL (the Pinned
  // rows deliberately duplicate their section counterparts) keeping the FIRST
  // copy, which is the decorated pinned one (badges, /calendar rewrite), and
  // drop sub-item accordions (a flat rail has no room for nested menus — the
  // parent link is the destination).
  // Roles without a purpose-built dashboard variant (frontdesk, manager) are
  // redirected from "/" to /calendar in clinic mode (see app/(dashboard)/
  // page.tsx) — their Dashboard nav row would be a disguised duplicate of the
  // Appointments link, so it's dropped with the same predicate.
  const hideDashboardEntry =
    simpleClinicMode && resolveDashboardRole(user?.roles) === "generic"

  /**
   * An affiliate partner's sidebar is exactly one row.
   *
   * The role holds a handful of permissions purely so it can READ its own
   * patients' records — `quotes.view` is the clear example: it unlocks the
   * Quotes rail on a patient page, and as a side effect unlocked a Sales >
   * Quotes row here, pointing at the clinic's whole quote board. Every
   * permission the role gains later would leak a row the same way, so the
   * partner's nav is built from an allowlist rather than filtered down: only
   * their own destination survives, whatever else they can technically read.
   *
   * Dashboard stays — "/" redirects a partner to their patients
   * (app/(dashboard)/page.tsx), so the row is their home affordance rather than
   * a second destination.
   *
   * Keyed on the ROLE, not on `leads.view.affiliated`: the permission is
   * grantable to any role from the roles screen, and an admin who was given it
   * (LelaDent 260904, to see referred patients) had their whole sidebar
   * collapse to these two rows. Same test as the "/" redirect and the mobile
   * escape — only an actual affiliator account gets the partner nav.
   */
  const PARTNER_NAV_URLS = ["/", "/affiliator/patients", "/affiliator/reports"]

  const isReferralPartner = hasRole(user?.roles, ROLES.AFFILIATOR)

  const partnerNavGroups = isReferralPartner
    ? filteredNavGroups
        .map((group) => ({
          ...group,
          items: group.items.filter((item) => PARTNER_NAV_URLS.includes(item.url)),
        }))
        .filter((group) => group.items.length > 0)
    : null

  /**
   * The filing roles — photographer and radiographer — get one row too.
   *
   * Same problem as the partner above, same fix, for the same reason. Both hold
   * `leads.view.all`, which is the ROW-VISIBILITY grant they need before
   * LeadPolicy will open a patient at all — and as a side effect it lit up the
   * Leads row here, pointing at the clinic's whole pipeline board. They work
   * from the Clients list; a lead is not a patient they photograph yet.
   *
   * Dashboard goes too, unlike the partner's: "/" redirects them to /clients
   * (app/(dashboard)/page.tsx), so the row would be a second door to the one
   * destination they have rather than a home affordance.
   *
   * 🟥 Keyed on the ROLE, not on a permission, and deliberately — the exact
   * trap documented above. `leads.photos.upload` is grantable to any role from
   * the roles screen, and the first clinic to give it to a coordinator so they
   * can file a picture would watch that coordinator's entire sidebar collapse
   * to one row. A clinic that builds its own photographer role adds it here.
   */
  const FILING_NAV_URLS = ["/clients"]

  const isFilingOnly =
    hasRole(user?.roles, ROLES.MARKETING) || hasRole(user?.roles, ROLES.RADIOGRAPHER)

  const filingNavGroups = isFilingOnly
    ? filteredNavGroups
        .map((group) => ({
          ...group,
          items: group.items.filter((item) => FILING_NAV_URLS.includes(item.url)),
        }))
        .filter((group) => group.items.length > 0)
    : null

  const displayNavGroups = partnerNavGroups ?? filingNavGroups ?? (simpleClinicMode
    ? (() => {
        const seen = new Set<string>()
        const flat = filteredNavGroups
          .flatMap((group) => group.items)
          .filter((item) => !(hideDashboardEntry && item.url === "/"))
          .filter((item) => !seen.has(item.url) && (seen.add(item.url), true))
          .map((item) => ({ ...item, items: undefined }))
        return flat.length > 0 ? [{ items: flat }] : []
      })()
    : filteredNavGroups)

  // Secondary nav items with dynamic badge for notifications
  const hasSettingsAccess = hasAnyPermission(SETTINGS_PERMISSIONS)
  // Notifications now lives as the header bell (next to the collapse control) —
  // see SidebarHeader below. Same gate as its old footer row, EXCEPT when
  // tasks are on: task assignment/overdue bells ride this surface, so a
  // simple-mode clinic with tasks_enabled gets its bell back.
  const showNotificationsBell =
    !simpleClinicMode || (user?.feature_flags?.tasks_enabled ?? false)

  // Admin plumbing (moved out of the main nav with the function-first
  // taxonomy): same gates the items carried as main-nav rows. Folded into a
  // single "Administration" flyout so laptop viewports don't lose ~4 footer
  // rows of nav space to rarely-visited pages.
  const adminItems = [
    ...(hasPermission("users.view")
      ? [
          {
            title: t("users"),
            url: "/users",
            icon: IconUsers,
          },
        ]
      : []),
    // Teams v1 — call-center agent teams admin. Gated by teams.manage AND the
    // per-clinic teams_enabled feature flag (mirrors the base declarative
    // permission/featureFlag gates, expressed as the spread).
    ...(hasPermission("teams.manage") && (user?.feature_flags?.teams_enabled ?? false)
      ? [
          {
            title: t("teams"),
            url: "/teams",
            icon: IconUsersGroup,
          },
        ]
      : []),
    // Team Activity / Agent Day Review — permission-gated only, no feature
    // flag: the migration grants `team-activity.view` to admin/manager/
    // team_leader, so the entry appears exactly where access does.
    ...(hasPermission("team-activity.view")
      ? [
          {
            title: t("teamActivity"),
            url: "/team-activity",
            icon: IconActivityHeartbeat,
          },
        ]
      : []),
    ...(hasPermission("activity-log.view") && !simpleClinicMode
      ? [
          {
            title: t("activityLog"),
            url: "/activity-log",
            icon: IconHistory,
          },
        ]
      : []),
  ]

  const navSecondary: NavSecondaryItem[] = [
    ...(adminItems.length > 0
      ? [
          {
            title: t("administration"),
            icon: IconShieldCog,
            items: adminItems,
          },
        ]
      : []),
    ...(hasSettingsAccess
      ? [
          {
            title: t("settings"),
            url: "/settings",
            icon: IconSettings,
          },
        ]
      : []),
  ]

  // Palette entries mirror the rail exactly (already gate-filtered and
  // translated), so ⌘K can only jump where the nav itself would.
  const searchEntries: NavSearchEntry[] = [
    ...displayNavGroups.flatMap((group) =>
      group.items.map((item) => ({
        group: (group as { label?: string }).label,
        title: item.title,
        url: item.url,
        icon: item.icon,
      }))
    ),
    // Flyout parents contribute their children (the parent row itself has no
    // URL); the header bell keeps Notifications reachable from the palette.
    ...navSecondary.flatMap((item) =>
      item.items
        ? item.items.map((child) => ({
            title: child.title,
            url: child.url,
            icon: child.icon,
          }))
        : [{ title: item.title, url: item.url!, icon: item.icon }]
    ),
    ...(showNotificationsBell
      ? [
          {
            title: t("notifications"),
            url: "/notifications",
            icon: IconBell,
          },
        ]
      : []),
  ]

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader className="gap-2.5 px-3 pt-3.5 pb-2 group-data-[collapsible=icon]:px-2">
        {/* One-line brand lockup (DenteX sidebar spec): wordmark · hairline ·
            clinic workspace name · collapse control. The single accent is the
            brand-blue gradient X — the same glyph as the product icon, which
            stands alone as the monogram on the collapsed rail. */}
        <div className="flex items-center gap-2.5 group-data-[collapsible=icon]:flex-col group-data-[collapsible=icon]:gap-1.5">
          <Link
            href="/"
            className="flex min-w-0 flex-1 items-center gap-2.5 group-data-[collapsible=icon]:flex-none"
          >
            <ProductGlyph className="hidden h-4 w-auto group-data-[collapsible=icon]:block" />
            {/* The supplied DenteX artwork, trimmed to its ink bounds so the
                lockup keeps its 15px cap height. The mark is a mid-blue that
                sinks into the dark rail, so it brightens with the theme —
                same intent as ProductGlyph's lifted gradient stops. */}
            <Image
              src="/dentex-wordmark.png"
              alt="DenteX"
              width={407}
              height={72}
              priority
              className="h-[15px] w-auto shrink-0 group-data-[collapsible=icon]:hidden dark:brightness-[1.45] dark:saturate-[1.15]"
            />
            <span className="h-[15px] w-px shrink-0 bg-border-strong group-data-[collapsible=icon]:hidden" />
            <span
              className="truncate text-[13px] font-semibold text-muted-foreground group-data-[collapsible=icon]:hidden"
              title={branding.name}
            >
              {branding.name}
            </span>
          </Link>
          {showNotificationsBell && (
            <Link
              href="/notifications"
              title={t("notifications")}
              className="relative flex size-7 shrink-0 items-center justify-center rounded-md text-sidebar-foreground/60 hover:bg-surface-3 hover:text-sidebar-foreground"
            >
              <IconBell className="size-[18px]" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 flex h-[15px] min-w-[15px] items-center justify-center rounded-full bg-destructive px-0.5 text-[9px] font-semibold tabular-nums text-white">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </Link>
          )}
          <SidebarTrigger className="size-7 shrink-0 text-sidebar-foreground/60" />
        </div>
        <NavSearch entries={searchEntries} />
      </SidebarHeader>
      <SidebarContent className="scrollbar-thin">
        <NavMain
          groups={displayNavGroups}
          workspaceLabel={simpleClinicMode ? undefined : t("groups.workspace")}
        />
      </SidebarContent>
      <SidebarFooter className="border-t border-sidebar-border">
        <NavSecondary items={navSecondary} className="p-0" />
        <AgentStatusSelector />
        <NavUser user={userData} />
      </SidebarFooter>
    </Sidebar>
  )
}
